import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import type { BulkMediaAccount } from './types'
import { LoadingBlock } from '../components/Spinner'

interface Props {
  accounts: BulkMediaAccount[] | null
  loadError?: string
  selected: string[]
  onChange: (ids: string[]) => void
  max: number
}

const fold = (s: string) => s.toLocaleLowerCase()

/** OnlyFans Accounts as one searchable list, with "select all" for what the search shows. */
export function AccountPicker({ accounts, loadError, selected, onChange, max }: Props) {
  const { t } = useTranslation('bulkMedia')
  const [q, setQ] = useState('')
  const picked = useMemo(() => new Set(selected), [selected])

  const shown = useMemo(() => {
    const query = fold(q.trim())
    return (accounts ?? [])
      .filter((a) => !query || [a.name, a.username].some((s) => fold(s).includes(query)))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  }, [accounts, q])

  const full = selected.length >= max
  const toggle = (id: string) => {
    if (picked.has(id)) onChange(selected.filter((x) => x !== id))
    else if (!full) onChange([...selected, id])
  }
  const on = shown.filter((a) => picked.has(a.account_id)).length
  const toggleShown = () => {
    const ids = shown.map((a) => a.account_id)
    if (on === shown.length) onChange(selected.filter((id) => !ids.includes(id)))
    else {
      const room = max - selected.length
      onChange([...selected, ...ids.filter((id) => !picked.has(id)).slice(0, Math.max(0, room))])
    }
  }

  const chosen = (accounts ?? []).filter((a) => picked.has(a.account_id))

  return (
    <div className="bm-accounts">
      {chosen.length > 0 && (
        <div className="bm-chosen" aria-label={t('accounts.chosen')}>
          {chosen.map((a) => (
            <button key={a.account_id} type="button" className="bm-pill" onClick={() => toggle(a.account_id)} title={t('accounts.remove')}>
              {a.name}
              <FeatherIcon name="x" size={12} />
            </button>
          ))}
          <button type="button" className="bm-link" onClick={() => onChange([])}>
            {t('clear')}
          </button>
        </div>
      )}
      <div className="bm-search">
        <FeatherIcon name="search" size={14} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('accounts.search')}
          aria-label={t('accounts.search')}
        />
      </div>
      {full && <p className="bm-hint">{t('accounts.max', { max })}</p>}
      <div className="bm-acclist" role="group" aria-label={t('accounts.title')}>
        {loadError && <p className="bm-error">{loadError}</p>}
        {!accounts && !loadError && <LoadingBlock pad="16px 0" size={20} label={t('loading')} />}
        {accounts && !shown.length && <p className="bm-muted">{t('accounts.none')}</p>}
        {shown.length > 1 && (
          <label className="bm-acc bm-acc--all">
            <input
              type="checkbox"
              checked={on === shown.length}
              ref={(el) => {
                if (el) el.indeterminate = on > 0 && on < shown.length
              }}
              onChange={toggleShown}
            />
            <span className="bm-acc-name">{q.trim() ? t('accounts.selectShown') : t('accounts.selectAll')}</span>
            <span className="bm-muted">
              {on ? `${on}/` : ''}
              {shown.length}
            </span>
          </label>
        )}
        {shown.map((a) => (
          <label key={a.account_id} className={`bm-acc ${picked.has(a.account_id) ? 'is-on' : ''}`}>
            <input
              type="checkbox"
              checked={picked.has(a.account_id)}
              disabled={!picked.has(a.account_id) && full}
              onChange={() => toggle(a.account_id)}
            />
            <span className="bm-acc-name">{a.name}</span>
            {a.username && <span className="bm-muted">@{a.username}</span>}
            {!a.is_logged_in && <span className="bm-tag">{t('accounts.loggedOut')}</span>}
          </label>
        ))}
      </div>
    </div>
  )
}
